import { useState } from 'react';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

/**
 * Viewport width the prerender assumes. The shells are rendered in Node, where there is no
 * viewport, so this constant is what every prerendered route is laid out against — and therefore
 * what the first client render has to agree with.
 */
export const SSR_WINDOW_WIDTH = 880;

/**
 * Viewport width, safe to hydrate against.
 *
 * Both call sites used to seed state straight from `window.innerWidth`. That was correct for a
 * pure SPA and wrong the moment the HTML was prerendered: the server lays out at
 * SSR_WINDOW_WIDTH, the client's first render used the real width, and callers switch *nav
 * structure* on the result — so React would hit a mismatch and throw the prerendered tree away.
 *
 * Starting from the same constant the server used keeps hydration clean, and the layout effect
 * corrects to the real width before the browser paints, so nobody sees the 880px layout.
 */
export function useWindowWidth(): number {
  const [width, setWidth] = useState(SSR_WINDOW_WIDTH);

  useIsomorphicLayoutEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return width;
}
